import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787/api/v1';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - add auth token
    this.client.interceptors.request.use((config) => {
      const token = useAuthStore.getState().token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(email: string, password: string) {
    const { data } = await this.client.post('/admin/login', { email, password });
    return data;
  }

  // Dashboard
  async getStats() {
    const { data } = await this.client.get('/admin/stats');
    return data;
  }

  // Orders
  async getOrders(params?: {
    page?: number;
    status?: string;
    search?: string;
    from_date?: string;
    to_date?: string;
  }) {
    const { data } = await this.client.get('/orders', { params });
    return data;
  }

  async getOrder(id: number) {
    const { data } = await this.client.get(`/orders/${id}`);
    return data;
  }

  async updateOrderStatus(id: number, status: string) {
    const { data } = await this.client.patch(`/orders/${id}/status`, { status });
    return data;
  }

  // Users
  async getUsers(params?: { page?: number; status?: string; search?: string }) {
    const { data } = await this.client.get('/admin/users', { params });
    return data;
  }

  async getUser(id: number) {
    const { data } = await this.client.get(`/admin/users/${id}`);
    return data;
  }

  async updateUserStatus(id: number, status: string) {
    const { data } = await this.client.patch(`/admin/users/${id}/status`, { status });
    return data;
  }

  // Drivers
  async getDrivers(params?: { page?: number; status?: string; search?: string }) {
    const { data } = await this.client.get('/admin/drivers', { params });
    return data;
  }

  async getDriver(id: number) {
    const { data } = await this.client.get(`/admin/drivers/${id}`);
    return data;
  }

  async approveDriver(id: number) {
    const { data } = await this.client.post(`/admin/drivers/${id}/approve`);
    return data;
  }

  async rejectDriver(id: number, reason: string) {
    const { data } = await this.client.post(`/admin/drivers/${id}/reject`, { reason });
    return data;
  }

  // Cafes
  async getCafes(params?: { page?: number; status?: string; search?: string }) {
    const { data } = await this.client.get('/admin/cafes', { params });
    return data;
  }

  async getCafe(id: number) {
    const { data } = await this.client.get(`/admin/cafes/${id}`);
    return data;
  }

  async updateCafeStatus(id: number, status: string) {
    const { data } = await this.client.patch(`/admin/cafes/${id}/status`, { status });
    return data;
  }

  // Categories
  async getCategories() {
    const { data } = await this.client.get('/categories');
    return data;
  }

  async createCategory(categoryData: FormData) {
    const { data } = await this.client.post('/admin/categories', categoryData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  async updateCategory(id: number, categoryData: FormData) {
    const { data } = await this.client.put(`/admin/categories/${id}`, categoryData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  async deleteCategory(id: number) {
    const { data } = await this.client.delete(`/admin/categories/${id}`);
    return data;
  }

  // Reports
  async getReports(params?: { from_date?: string; to_date?: string }) {
    const { data } = await this.client.get('/admin/reports', { params });
    return data;
  }
}

export const api = new ApiService();
